import React from "react";
import { Box, BoxProps, Flex, Text } from "@chakra-ui/react";
import { useLandingTheme } from "../../LandingThemeContext";
import { ThemedPanel } from "./ThemedLayout";

export type QuestionMockFormat = "multipleChoice" | "freeResponse" | "caseScenario";

const MULTIPLE_CHOICE = {
  stem: "In which part of the digestive system does most nutrient absorption occur?",
  options: ["Stomach", "Small intestine", "Large intestine", "Esophagus"],
  correctOptionIndex: 1,
};

const CASE_SCENARIO = {
  scenario: "A 34-year-old reports months of fatigue. Bloodwork shows iron-deficiency anemia despite a normal diet.",
  stem: "Damage to which region best explains the absorption problem?",
  options: ["Gastric fundus", "Duodenum", "Sigmoid colon", "Esophageal sphincter"],
  correctOptionIndex: 1,
};

const FREE_RESPONSE = {
  stem: "Explain why the small intestine, not the stomach, is the main site of nutrient absorption.",
  placeholder: "Type your answer — Jungle grades it and explains what you missed.",
};

function OptionRow({ letter, label, isCorrect, revealed }: { letter: string; label: string; isCorrect: boolean; revealed: boolean }) {
  const theme = useLandingTheme();
  const showCorrect = revealed && isCorrect;
  return (
    <Flex
      align="center"
      gap="12px"
      px="14px"
      py="11px"
      borderTop={`1px solid ${theme.color.border}`}
      bg={showCorrect ? theme.color.accentSoft : "transparent"}
      fontFamily={theme.font.body}
      fontSize="15px"
      color={theme.isDark ? theme.color.heading : theme.color.text}
    >
      <Text fontWeight={600} color={showCorrect ? theme.color.success : theme.color.muted} w="14px">
        {letter}
      </Text>
      <Text flex="1">{label}</Text>
      {showCorrect && (
        <Flex align="center" color={theme.color.success} aria-label="Correct answer">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8.5l3.2 3L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Flex>
      )}
    </Flex>
  );
}

// A generated question, built in the page's own type so it sits in the design rather than on it.
export function QuestionCardMock({
  format = "multipleChoice",
  revealed = true,
  showFormatLabel = true,
  ...props
}: BoxProps & { format?: QuestionMockFormat; revealed?: boolean; showFormatLabel?: boolean }) {
  const theme = useLandingTheme();
  const stemStyle = {
    fontFamily: theme.font.body,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: 1.45,
    color: theme.isDark ? theme.color.heading : theme.color.heading,
  } as const;

  return (
    <ThemedPanel overflow="hidden" display="flex" flexDirection="column" {...props}>
      <Box px="18px" pt="18px" pb="14px">
        {showFormatLabel && (
          <Text fontFamily={theme.font.mono} fontSize="11px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color={theme.color.muted} mb="10px">
            {format === "multipleChoice" && "Multiple choice"}
            {format === "freeResponse" && "Free response"}
            {format === "caseScenario" && "Case scenario"}
          </Text>
        )}
        {format === "caseScenario" && (
          <Text fontFamily={theme.font.body} fontSize="14px" lineHeight={1.5} color={theme.color.text} mb="10px">
            {CASE_SCENARIO.scenario}
          </Text>
        )}
        <Text {...stemStyle}>
          {format === "multipleChoice" && MULTIPLE_CHOICE.stem}
          {format === "caseScenario" && CASE_SCENARIO.stem}
          {format === "freeResponse" && FREE_RESPONSE.stem}
        </Text>
      </Box>

      {format === "multipleChoice" &&
        MULTIPLE_CHOICE.options.map((option, optionIndex) => (
          <OptionRow key={option} letter={"ABCD"[optionIndex]} label={option} isCorrect={optionIndex === MULTIPLE_CHOICE.correctOptionIndex} revealed={revealed} />
        ))}

      {format === "caseScenario" &&
        CASE_SCENARIO.options.map((option, optionIndex) => (
          <OptionRow key={option} letter={"ABCD"[optionIndex]} label={option} isCorrect={optionIndex === CASE_SCENARIO.correctOptionIndex} revealed={revealed} />
        ))}

      {format === "freeResponse" && (
        <Box borderTop={`1px solid ${theme.color.border}`} px="18px" py="16px">
          <Box
            border={`1px solid ${theme.color.border}`}
            borderRadius="6px"
            px="12px"
            py="10px"
            minH="76px"
            fontFamily={theme.font.body}
            fontSize="14px"
            color={theme.color.muted}
          >
            {FREE_RESPONSE.placeholder}
          </Box>
          <Text mt="10px" fontFamily={theme.font.body} fontSize="13px" fontWeight={600} color={theme.color.accent}>
            Get feedback →
          </Text>
        </Box>
      )}
    </ThemedPanel>
  );
}
